import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Empty,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { Collapse } from "../../../ui/primitives/Collapse";
import { Progress } from "../../../ui/primitives/Progress";
import { Button as PrimitiveButton } from "../../../ui/primitives/Button";
import { Stack as PrimitiveStack } from "../../../ui/primitives/Stack";
import { Text as PrimitiveText } from "../../../ui/primitives/Text";
import StaffDailySummary from "./StaffDailySummary";
import WorkBriefModal from "./WorkBriefModal";
import { useStaffIdentity } from "../../../common/staff/StaffIdentityProvider";
import { useShiftChecklistService } from "../../../services/members/ShiftChecklistServiceProvider";
import { useRotaService } from "../../../services/members/RotaServiceProvider";
import { useCallsService } from "../../../services/members/CallsServiceProvider";
import { useMemberService } from "../../../services/members/MemberServiceProvider";
import { useBdOrganisationService } from "../../../services/members/BdOrganisationServiceProvider";
import {
  SHIFT_BLOCK_LABELS,
  TodayShift,
  TodayShiftBlock,
} from "../../../model/members/ShiftChecklist";
import { Allocation, RotaSlot } from "../../../model/members/Rota";
// Same helper the rota uses, so one allocation doesn't describe itself two ways on the
// two screens that show it — and an untitled task reads as "Task" here too, rather than
// rendering as a bare tag with nothing after it.
import { allocationLabel, formatShiftWindow } from "../../../utils/rotaUtils";
import { Call } from "../../../model/members/Calls";
import { BdOrganisation } from "../../../model/members/BdOrganisation";
import { fromDateOnly } from "../../../utils/DateOnly";

const { Title, Text } = Typography;

function formatTime(value: string | null): string {
  if (!value) return "";
  // completedAt is an ISO datetime (or a revived Date); dayjs handles both.
  const d = dayjs(value);
  return d.isValid() ? d.format("HH:mm") : "";
}

function formatWindow(shift: TodayShift): string {
  if (shift.startTime && shift.endTime) return `${shift.startTime}–${shift.endTime}`;
  return shift.startTime ?? shift.endTime ?? "";
}

interface Totals {
  done: number;
  total: number;
  percent: number;
}

// True when the event came from a control nested inside a clickable row. Both the work
// rows and the BD rows are themselves click targets, and each now contains a Sign off
// button — without this the row's handler would fire too (navigating away from the toast,
// or opening the brief instead of signing off on the keyboard path).
function isFromNestedControl(e: React.SyntheticEvent): boolean {
  return (e.target as HTMLElement).closest("button") != null;
}

function totalsOf(blocks: TodayShiftBlock[]): Totals {
  const done = blocks.reduce((n, b) => n + b.doneCount, 0);
  const total = blocks.reduce((n, b) => n + b.totalCount, 0);
  return { done, total, percent: total === 0 ? 0 : Math.round((done / total) * 100) };
}

const StaffDaily: React.FC = () => {
  const service = useShiftChecklistService();
  const rotaService = useRotaService();
  const callsService = useCallsService();
  const memberService = useMemberService();
  const bdOrganisationService = useBdOrganisationService();
  const navigate = useNavigate();
  const { selectedStaffId } = useStaffIdentity();
  const [shifts, setShifts] = useState<TodayShift[]>([]);
  const [myShifts, setMyShifts] = useState<RotaSlot[]>([]);
  const [myWork, setMyWork] = useState<Allocation[]>([]);
  // The allocation whose full brief is open, or null. Briefs live behind a click so the
  // widget stays scannable — see WorkBriefModal.
  const [briefAllocation, setBriefAllocation] = useState<Allocation | null>(null);
  const [myCalls, setMyCalls] = useState<Call[]>([]);
  // How many of my calls I have already dealt with today. The open-calls queue can't say —
  // a completed call drops straight out of it — so this comes from the same assignment-state
  // read the CRM colours rows with.
  //
  // Note the two figures on the card are dated differently: the queue is bounded by the
  // browser's day, this one by the CLUB's (resolved server-side). Around midnight, or for
  // someone working from another timezone, they can briefly describe different days. Left as
  // is because aligning them means changing what /dashboard/calls means for every caller —
  // tracked as the call-time-windows follow-up on FORT-358, not smuggled in here.
  const [loggedToday, setLoggedToday] = useState<number>(0);
  // FORT-363: my BD organisations whose follow-up is due today or overdue. Listed
  // individually, unlike calls — the client's notes keep BD items and tasks itemised
  // and collapse only the membership calls into a summary.
  const [myBdDue, setMyBdDue] = useState<BdOrganisation[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // Keyed "kind-id", not a bare number: checklist items, staff tasks and BD organisations
  // are three independent id spaces, so a bare id put a spinner on BD organisation 7 while
  // task 7 was saving, and disabled every sign-off button whenever a checklist item ticked.
  const [busyItemId, setBusyItemId] = useState<string | null>(null);
  // Shift procedures are open unless the user has closed them, so the state tracks what is
  // *shut* rather than what is open. Tracking the open ones instead would mean seeding them
  // from a load — which paints one collapsed frame before the seed lands, and re-opens
  // anything closed every time ticking an item reloads the day.
  const [closedShiftIds, setClosedShiftIds] = useState<Set<string>>(new Set());
  // Monotonic load counter — see load(). Only the newest in-flight load may commit.
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    // Switching person twice in quick succession (pick the wrong name, correct it) fires
    // two loads whose responses can land out of order. Only the newest may commit, or the
    // slower earlier one overwrites it and the screen settles on the wrong person's day —
    // the exact failure this reload exists to prevent.
    const seq = ++loadSeq.current;
    setLoading(true);
    setError(null);
    try {
      const today = await service.getToday(dayjs().format("YYYY-MM-DD"));
      if (seq !== loadSeq.current) return;
      setShifts(today);
      // Best-effort: the rota "your shift today" bar must not break the checklist load
      // (e.g. an account not linked to a staff profile returns 409).
      try {
        const day = dayjs().format("YYYY-MM-DD");
        const [shift, work, calls, states, bdDue] = await Promise.all([
          rotaService.getMyToday(day),
          rotaService.getMyAllocationsToday(day),
          callsService.getMyCalls(day),
          // Best-effort on its own: a missing logged-today count is a missing subtitle,
          // not a reason to blank the calls card.
          memberService.getMyCallAssignmentStates().catch(() => []),
          // Likewise: losing the BD block must not cost the staff member their calls or
          // their checklist. The service resolves "mine" server-side.
          bdOrganisationService.getMyDueOrganisations().catch(() => [] as BdOrganisation[]),
        ]);
        if (seq !== loadSeq.current) return;
        setMyShifts(shift);
        setMyWork(work);
        setMyCalls(calls);
        setLoggedToday(states.filter((s) => s.state === "Completed").length);
        setMyBdDue(bdDue);
      } catch {
        if (seq !== loadSeq.current) return;
        setMyShifts([]);
        setMyWork([]);
        setMyCalls([]);
        setLoggedToday(0);
        setMyBdDue([]);
      }
    } catch (e) {
      if (seq !== loadSeq.current) return;
      setError(e instanceof Error ? e.message : "Failed to load today's shifts.");
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [service, rotaService, callsService, memberService, bdOrganisationService]);

  // Reloads when the staff picker changes who this device is signed in as. Everything on
  // this screen except the shift procedures is "your work today" — your shift, your
  // allocations, your calls — so after switching person it is showing someone else's day
  // until it refetches. Making the user notice and press Refresh is how the wrong person's
  // work gets ticked off.
  //
  // The per-person sections are emptied first rather than left up during the round-trip:
  // they stay interactive while loading, and a "Reached" click in that window would post
  // the previous person's call under the new identity.
  useEffect(() => {
    setMyShifts([]);
    setMyWork([]);
    setMyCalls([]);
    setMyBdDue([]);
    // The next person on a shared device starts from a fully open checklist — inheriting
    // whatever the previous one folded away is how a procedure goes unread.
    setClosedShiftIds(new Set());
    load();
  }, [load, selectedStaffId]);

  // Every shift procedure starts expanded: the checklist is the job, and a panel that has
  // to be opened first is a panel that gets forgotten. Derived during render, so the first
  // paint after a load is already open — no collapsed flash, no expand animation.
  const openShiftIds = useMemo(
    () =>
      shifts.map((s) => String(s.shiftDefinitionId)).filter((id) => !closedShiftIds.has(id)),
    [shifts, closedShiftIds]
  );

  const onCollapseChange = useCallback(
    (keys: string | string[]) => {
      const open = new Set(Array.isArray(keys) ? keys : [keys]);
      setClosedShiftIds(
        new Set(
          shifts.map((s) => String(s.shiftDefinitionId)).filter((id) => !open.has(id))
        )
      );
    },
    [shifts]
  );

  // Everything on today's shifts, as one number — the checklist is spread over several
  // shifts and blocks, so "how much of today is left" isn't readable from any one of them.
  const overall = useMemo(() => totalsOf(shifts.flatMap((s) => s.blocks)), [shifts]);

  const toggleItem = useCallback(
    async (itemId: number, done: boolean) => {
      setBusyItemId(`item-${itemId}`);
      try {
        await service.setItemCompletion(itemId, done, dayjs().format("YYYY-MM-DD"));
        await load();
      } catch {
        message.error("Failed to update item");
      } finally {
        setBusyItemId(null);
      }
    },
    [service, load]
  );

  // FORT-367: sign an assigned task off as done.
  //
  // FORT-374 extended sign-off to BizDev allocations (see signOffBizDev below), so this now
  // covers only the Task half. Calls are still excluded: a call is completed by logging its
  // outcome in the CRM, and a tick here would be a second, competing way to record the same
  // thing.
  const signOffTask = useCallback(
    async (taskId: number) => {
      setBusyItemId(`task-${taskId}`);
      try {
        await rotaService.completeTask(taskId);
        message.success("Signed off");
        await load();
      } catch {
        // The write is assignee-scoped server-side, so the common failure here is a task
        // that isn't mine — which the UI shouldn't have offered in the first place.
        message.error("Failed to sign off task");
      } finally {
        setBusyItemId(null);
      }
    },
    [rotaService, load]
  );

  // FORT-374: sign a Business Development *allocation* off as done — the focus-area work
  // Harvey allocates on the rota (dbo.staff_allocation), not a BD organisation follow-up.
  // FORT-367 left this type without a done state on the reasoning that a focus area isn't a
  // unit of work; the client has since said it is, and staff need to mark it finished.
  //
  // Separate handler rather than a branch inside signOffTask: the two hit different
  // endpoints, and folding them together would mean a single catch reporting "task" for a
  // BizDev failure.
  const reopenBizDev = useCallback(
    async (allocationId: number) => {
      try {
        await rotaService.reopenBizDevAllocation(allocationId);
        message.success("Sign-off undone");
        await load();
      } catch {
        message.error("Failed to undo sign-off");
      }
    },
    [rotaService, load]
  );

  const signOffBizDev = useCallback(
    async (allocationId: number) => {
      setBusyItemId(`bizdev-${allocationId}`);
      try {
        await rotaService.signOffBizDevAllocation(allocationId);
        // Undo lives on the toast rather than on a row, because signing off is what removes
        // the row: the allocation leaves this list AND the manager's week grid, so the moment
        // of the click is the last one where anything on screen still knows its id.
        message.success({
          content: (
            <span>
              Signed off{" "}
              <PrimitiveButton
                variant="link"
                size="sm"
                data-testid={`staff-daily-undo-bizdev-${allocationId}`}
                onClick={() => reopenBizDev(allocationId)}
              >
                Undo
              </PrimitiveButton>
            </span>
          ),
          duration: 6,
        });
        await load();
      } catch {
        // Assignee-scoped server-side, so the common failure is work that isn't mine —
        // which the UI shouldn't have offered in the first place.
        message.error("Failed to sign off work");
      } finally {
        setBusyItemId(null);
      }
    },
    [rotaService, load, reopenBizDev]
  );

  // FORT-367: sign a BD follow-up off as done. Reverses the FORT-363 decision that
  // completion should be implicit in the BD CRM's normal save — the client asked for an
  // explicit button, so the count now drains from here too.
  const signOffBd = useCallback(
    async (organisationId: number) => {
      setBusyItemId(`bd-${organisationId}`);
      try {
        await bdOrganisationService.signOffOrganisation(organisationId);
        message.success("Signed off");
        await load();
      } catch {
        message.error("Failed to sign off follow-up");
      } finally {
        setBusyItemId(null);
      }
    },
    [bdOrganisationService, load]
  );

  // FORT-361: sales calls are summarised by the calls card above, so they are dropped from
  // the work list — the client had the same calls in both places and asked for one. Task and
  // BizDev allocations are unaffected; they have no summary of their own.
  const nonCallWork = useMemo(() => myWork.filter((a) => a.kind !== "Call"), [myWork]);

  /**
   * FORT-374: the sign-off control on a work row — Task and BizDev, which hit different
   * endpoints and so carry different busy keys and testids.
   *
   * Calls have no sign-off: they complete by logging an outcome in the CRM, and giving them a
   * tick here would be a second, competing way to record the same thing. They never reach
   * this function anyway — nonCallWork filters them out upstream — so this returns null for
   * them rather than asserting a branch that can't be hit.
   */
  const renderSignOff = (a: Allocation) => {
    if (a.kind === "Call") return null;
    const isBizDev = a.kind === "BizDev";
    const busyKey = isBizDev ? `bizdev-${a.id}` : `task-${a.id}`;
    return (
      <PrimitiveButton
        size="sm"
        data-testid={`staff-daily-signoff-${isBizDev ? "bizdev" : "task"}-${a.id}`}
        loading={busyItemId === busyKey}
        disabled={busyItemId != null && busyItemId !== busyKey}
        // The row itself opens the brief when it has one; without this the click would sign
        // the work off AND open the modal behind the toast.
        onClick={(e) => {
          e.stopPropagation();
          if (isBizDev) signOffBizDev(a.id);
          else signOffTask(a.id);
        }}
        style={{ marginLeft: 8 }}
      >
        Sign off
      </PrimitiveButton>
    );
  };

  const renderBlock = (shift: TodayShift, block: TodayShiftBlock) => {
    const percent = block.totalCount === 0 ? 0 : Math.round((block.doneCount / block.totalCount) * 100);
    return (
      <div
        key={block.block}
        data-testid={`staff-daily-block-${shift.shiftDefinitionId}-${block.block}`}
        style={{ marginBottom: 16 }}
      >
        <Space align="center" style={{ justifyContent: "space-between", width: "100%" }}>
          <Text strong>{SHIFT_BLOCK_LABELS[block.block]}</Text>
          <Text type="secondary">
            {block.doneCount}/{block.totalCount}
          </Text>
        </Space>
        <Progress percent={percent} size="small" />
        <Space direction="vertical" style={{ width: "100%", marginTop: 8 }}>
          {block.items.map((item) => (
            <Checkbox
              key={item.itemId}
              data-testid={`staff-daily-item-${item.itemId}`}
              checked={item.isDone}
              disabled={busyItemId === `item-${item.itemId}`}
              onChange={(e) => toggleItem(item.itemId, e.target.checked)}
            >
              {item.label}
              {item.isDone && item.completedByStaffName ? (
                <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                  ✓ {item.completedByStaffName} {formatTime(item.completedAt)}
                </Text>
              ) : null}
            </Checkbox>
          ))}
        </Space>
      </div>
    );
  };

  return (
    <div data-testid="staff-daily" style={{ padding: 24, maxWidth: 900, margin: "0 auto" }}>
      <Space align="center" style={{ justifyContent: "space-between", width: "100%", marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          Staff Daily — {dayjs().format("dddd D MMMM YYYY")}
        </Title>
        <Button icon={<ReloadOutlined />} onClick={load} data-testid="staff-daily-reload">
          Refresh
        </Button>
      </Space>

      {myShifts.length > 0 ? (
        <Alert
          type="info"
          showIcon
          data-testid="staff-daily-shift-bar"
          style={{ marginBottom: 16 }}
          message={`Your shift today: ${myShifts.map(formatShiftWindow).join(", ")}`}
        />
      ) : null}

      {overall.total > 0 || nonCallWork.length > 0 || myCalls.length > 0 || myBdDue.length > 0 ? (
        <StaffDailySummary
          doneCount={overall.done}
          totalCount={overall.total}
          allocatedCount={nonCallWork.length}
          bdCount={myBdDue.length}
          callsCount={myCalls.length}
        />
      ) : null}

      <Title level={4} data-testid="staff-daily-work-heading" style={{ marginTop: 0, marginBottom: 12 }}>
        Your work today
      </Title>

      {nonCallWork.length > 0 ? (
        <Card size="small" data-testid="staff-daily-work" style={{ marginBottom: 16 }}>
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            {/*
              Category + title only — never the brief. A detailed brief would stretch this
              widget until the day's work stopped being scannable, which is the whole point of
              splitting the two views (FORT-351). Clicking opens the full brief.
            */}
            {nonCallWork.map((a) => {
              // Only rows that actually carry a brief are interactive. Advertising every row
              // as a button — and taking keyboard focus for it — just to open "no brief was
              // added" is a promise the modal can't keep.
              const hasBrief = !!a.brief?.trim();
              return (
              <div
                key={`${a.kind}-${a.id}`}
                data-testid={`staff-daily-work-${a.kind}-${a.id}`}
                role={hasBrief ? "button" : undefined}
                tabIndex={hasBrief ? 0 : undefined}
                onClick={hasBrief ? (e) => { if (isFromNestedControl(e)) return; setBriefAllocation(a); } : undefined}
                onKeyDown={
                  hasBrief
                    ? (e) => {
                        // Without this, Enter on the nested Sign off button bubbles here,
                        // gets preventDefault()ed — cancelling the button's own activation —
                        // and opens the brief instead of signing the task off.
                        if (isFromNestedControl(e)) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setBriefAllocation(a);
                        }
                      }
                    : undefined
                }
                style={{ cursor: hasBrief ? "pointer" : "default" }}
              >
                <Tag color={a.kind === "BizDev" ? "purple" : "blue"}>
                  {a.kind === "BizDev" ? "Business Development" : "Task"}
                </Tag>
                {allocationLabel(a)}
                {hasBrief ? (
                  <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                    — read brief
                  </Text>
                ) : null}
                {renderSignOff(a)}
              </div>
              );
            })}
          </Space>
        </Card>
      ) : null}

      {/*
        Shown while there are calls left OR any were logged today. Gating on the queue alone
        made the card — and the day's whole call record — disappear the moment the last call
        was logged, which reads as "that never happened" right after the work was done.
      */}
      {myCalls.length > 0 || loggedToday > 0 ? (
        <Card
          size="small"
          title="Calls to make"
          data-testid="staff-daily-calls"
          style={{ marginBottom: 16 }}
        >
          {/*
            A count and a way in, nothing else (FORT-361). The client had the same calls
            listed twice on this page and asked for one summary; the per-lead detail — and
            since this ticket, the outcome buttons — live in the CRM, which is where a lead is
            actually worked. Any of those updates completes the call, so the count drains as
            they work rather than needing its own controls here.
          */}
          <PrimitiveStack gap={2}>
            <PrimitiveText weight="semibold" data-testid="staff-daily-calls-count">
              {myCalls.length === 0
                ? "All calls done"
                : `${myCalls.length} ${myCalls.length === 1 ? "call" : "calls"} to make`}
            </PrimitiveText>
            {loggedToday > 0 ? (
              <PrimitiveText color="secondary" data-testid="staff-daily-calls-logged">
                {loggedToday} logged today
              </PrimitiveText>
            ) : null}
            {myCalls.length > 0 ? (
              <div>
                <PrimitiveButton
                  variant="primary"
                  data-testid="staff-daily-start-calls"
                  onClick={() => navigate("/members/retention?mine=1")}
                >
                  Start calls
                </PrimitiveButton>
              </div>
            ) : null}
          </PrimitiveStack>
        </Card>
      ) : null}

      {/*
        FORT-363: Business development follow-ups. Listed individually rather than summarised
        like the calls above — the client's notes keep BD items and ad-hoc tasks itemised and
        collapse only the membership calls, because a BD follow-up is a handful of named
        organisations rather than a queue of dozens.

        FORT-367 reverses this block's original "no inline mark done" decision. Completion
        used to be implicit in the BD CRM's normal save (stamp last-contacted, clear the
        date); the client asked for an explicit sign-off button, so there is now one here.
        It is the same act, not a competing one — the endpoint clears the follow-up date
        exactly as the manual save did, and additionally records who signed it off.
      */}
      {myBdDue.length > 0 ? (
        <Card
          size="small"
          title="Business development follow-ups"
          data-testid="staff-daily-bd"
          extra={<Tag color="purple" data-testid="staff-daily-bd-count">{myBdDue.length} due</Tag>}
          style={{ marginBottom: 16 }}
        >
          <PrimitiveStack gap={2}>
            <PrimitiveText color="secondary">
              Contact these organisations today — log the outcome in the BD CRM after.
            </PrimitiveText>

            {myBdDue.map((organisation) => {
              // The server decides what's due (follow_up_date <= its today); this only
              // splits that set into overdue vs due-today, and does so against the browser's
              // day — so around midnight the two can disagree by a day, exactly as noted for
              // the calls card above. The tag is omitted entirely when there's no date, since
              // the type allows it even though the due-only read never returns one.
              // isValid() as well as null: fromDateOnly hands back an *invalid* Dayjs for an
              // unparseable value rather than null, and an invalid date is neither before nor
              // after today — so without this it would render a confident "Due today".
              const parsed = organisation.followUpDate ? fromDateOnly(organisation.followUpDate) : null;
              const due = parsed != null && parsed.isValid() ? parsed : null;
              const overdue = due != null && due.startOf("day").isBefore(dayjs().startOf("day"));
              return (
                <div
                  key={organisation.id}
                  data-testid={`staff-daily-bd-${organisation.id}`}
                  role="button"
                  tabIndex={0}
                  // Named individually rather than left as N identical "button"s: every row
                  // goes to the same filtered list (there is no per-organisation deep link),
                  // so without a name a keyboard user tabs through several controls that
                  // announce nothing to tell them apart.
                  aria-label={`Open ${organisation.name} in the Business Development CRM`}
                  style={{ cursor: "pointer" }}
                  onClick={(e) => {
                    if (isFromNestedControl(e)) return;
                    navigate("/members/business-development?mine=1");
                  }}
                  onKeyDown={(e) => {
                    // See the work row above: the nested Sign off button owns its own keys.
                    if (isFromNestedControl(e)) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate("/members/business-development?mine=1");
                    }
                  }}
                >
                  <Space wrap align="center">
                    <Text strong>{organisation.name}</Text>
                    <Text type="secondary">
                      {[organisation.category, organisation.contactName].filter(Boolean).join(" · ")}
                    </Text>
                    {due != null ? (
                      <Tag color={overdue ? "red" : "gold"}>{overdue ? "Overdue" : "Due today"}</Tag>
                    ) : null}
                    <PrimitiveButton
                      size="sm"
                      data-testid={`staff-daily-signoff-bd-${organisation.id}`}
                      loading={busyItemId === `bd-${organisation.id}`}
                      disabled={busyItemId != null && busyItemId !== `bd-${organisation.id}`}
                      // The row navigates to the BD CRM; without this the sign-off click
                      // would also leave the page before the toast could be read.
                      onClick={(e) => {
                        e.stopPropagation();
                        signOffBd(organisation.id);
                      }}
                    >
                      Sign off
                    </PrimitiveButton>
                  </Space>
                </div>
              );
            })}

            <div>
              <PrimitiveButton
                variant="primary"
                data-testid="staff-daily-start-bd"
                onClick={() => navigate("/members/business-development?mine=1")}
              >
                Start business development
              </PrimitiveButton>
            </div>
          </PrimitiveStack>
        </Card>
      ) : null}

      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} /> : null}

      {/* Feedback item 7: the day's allocated work is the point of this page, so it sits at
          the top and stays visible. The standing procedures below it are a reference, not
          the job. */}
      {nonCallWork.length === 0 && myCalls.length === 0 && loggedToday === 0 && myBdDue.length === 0 ? (
        <Empty
          data-testid="staff-daily-no-work"
          description="Nothing allocated to you today."
        />
      ) : null}

      {/* The shift procedures sit below the allocated work, but open — they're the same
          every day, which is exactly why an unopened panel gets skipped. */}
      {loading && shifts.length === 0 ? (
        <div style={{ textAlign: "center", padding: 48 }}>
          <Spin />
        </div>
      ) : shifts.length === 0 ? (
        <Text type="secondary" data-testid="staff-daily-empty">
          No shift procedures configured yet. A club owner can set these up in CRM → Shift Checklists.
        </Text>
      ) : (
        <div data-testid="staff-daily-procedures" style={{ marginTop: 32 }}>
          <Text type="secondary" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Shift procedures
          </Text>
          <div style={{ marginTop: 8 }}>
            <Collapse
              size="small"
              data-testid="staff-daily-procedures-collapse"
              activeKey={openShiftIds}
              onChange={onCollapseChange}
              items={shifts.map((shift) => {
                const shiftTotals = totalsOf(shift.blocks);
                return {
                  key: String(shift.shiftDefinitionId),
                  label: (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        width: "100%",
                      }}
                    >
                      <Space size={8} data-testid={`staff-daily-procedure-${shift.shiftDefinitionId}`}>
                        <span style={{ fontSize: 13 }}>{shift.shiftName}</span>
                        {formatWindow(shift) ? (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {formatWindow(shift)}
                          </Text>
                        ) : null}
                        {shift.isUrgent ? <Tag color="red">Urgent</Tag> : null}
                      </Space>
                      {/* The count stays in the header so a shift someone has collapsed
                          still says where it got to. */}
                      <Space size={8} align="center">
                        <Text
                          type="secondary"
                          style={{ fontSize: 12, whiteSpace: "nowrap" }}
                          data-testid={`staff-daily-procedure-count-${shift.shiftDefinitionId}`}
                        >
                          {shiftTotals.done}/{shiftTotals.total}
                        </Text>
                        <Progress
                          percent={shiftTotals.percent}
                          size="small"
                          showInfo={false}
                          style={{ width: 72, marginBottom: 0 }}
                        />
                      </Space>
                    </div>
                  ),
                  children: <div>{shift.blocks.map((block) => renderBlock(shift, block))}</div>,
                };
              })}
            />
          </div>
        </div>
      )}

      <WorkBriefModal allocation={briefAllocation} onClose={() => setBriefAllocation(null)} />
    </div>
  );
};

export default StaffDaily;
